import { CategorizedItem, CostRange } from '../services/reportsApi';

/** Live cost edits keyed by itemId: saved overrides plus whatever the user is typing. */
export type CostEdits = Record<string, number>;

export interface CategorySummary {
  category: string;
  count: number;
  costRange: CostRange;
}

/**
 * The page's own summary, computed from the filtered items. Separate from the wire type
 * `ReportSummaryStats` so the range total is required rather than optional.
 */
export interface SeverityStats {
  highCount: number;
  mediumCount: number;
  lowCount: number;
  totalItems: number;
  totalRepairCostRange: CostRange;
}

const pointRange = (v: number): CostRange => ({ low: v, expected: v, high: v });

/**
 * The range an item contributes to totals, or null when it contributes nothing.
 *
 * Two modes, chosen by whether `liveEdits` is passed:
 * - With a map (the report page): the map is the whole truth about edits, including
 *   hydrated saved ones. An item missing from the map is unedited, even if the payload
 *   still carries an editedCostRange (e.g. just after Reset, before the refetch lands).
 * - Without a map: the saved editedCostRange is used.
 *
 * An edit wins and counts even on a contractor-eval item. It is rounded and clamped the
 * way the server does (`postprocessing.edited_cost_range`), so page and email totals
 * agree. Otherwise contractor-eval items have no price. A missing costRange (pre-07
 * payload or a hand-built fixture) falls back to estimatedCost as a point range; the
 * client never derives a band.
 */
export function itemCostRange(item: CategorizedItem, liveEdits?: CostEdits): CostRange | null {
  const edit = liveEdits?.[item.itemId];
  if (edit !== undefined) return pointRange(Math.max(0, Math.round(edit)));
  if (!liveEdits && item.editedCostRange) return item.editedCostRange;
  if (item.needsContractorEvaluation === true) return null;
  if (item.costRange !== undefined) return item.costRange;
  return pointRange(item.estimatedCost ?? 0);
}

/** Sum of `itemCostRange` over the items; an edit adds its single value to all three bounds. */
export function sumCostRanges(items: CategorizedItem[], liveEdits?: CostEdits): CostRange {
  const total = pointRange(0);
  for (const item of items) {
    const r = itemCostRange(item, liveEdits);
    if (!r) continue;
    total.low += r.low;
    total.expected += r.expected;
    total.high += r.high;
  }
  return total;
}

export function groupItemsByCategory(
  items: CategorizedItem[],
  liveEdits?: CostEdits,
): CategorySummary[] {
  if (!items || items.length === 0) return [];

  const categories = new Map<string, CategorySummary>();

  for (const item of items) {
    // Items with no price (unedited contractor-eval) are left out of both count and
    // total, the same rule as the header total and the email.
    const r = itemCostRange(item, liveEdits);
    if (!r) continue;
    const category = item.category || 'Uncategorized';

    const existing = categories.get(category);
    if (existing) {
      existing.count += 1;
      existing.costRange.low += r.low;
      existing.costRange.expected += r.expected;
      existing.costRange.high += r.high;
    } else {
      categories.set(category, { category, count: 1, costRange: { ...r } });
    }
  }

  const summary = Array.from(categories.values());
  summary.sort((a, b) => {
    if (b.costRange.expected !== a.costRange.expected) {
      return b.costRange.expected - a.costRange.expected;
    }
    return a.category.localeCompare(b.category);
  });

  return summary;
}

export function calculateSeverityStats(
  items: CategorizedItem[],
  liveEdits?: CostEdits,
): SeverityStats {
  if (!items || items.length === 0) {
    return { highCount: 0, mediumCount: 0, lowCount: 0, totalItems: 0, totalRepairCostRange: pointRange(0) };
  }

  let highCount = 0;
  let mediumCount = 0;
  let lowCount = 0;

  for (const item of items) {
    const s = (item.severity || '').toLowerCase();
    if (s === 'high' || s === 'critical' || s === 'severe') {
      highCount++;
    } else if (s === 'medium' || s === 'moderate') {
      mediumCount++;
    } else {
      lowCount++;
    }
  }

  // Contractor-eval items count in the severity buckets (they're real findings);
  // itemCostRange leaves the unedited ones out of the total.
  return {
    highCount,
    mediumCount,
    lowCount,
    totalItems: items.length,
    totalRepairCostRange: sumCostRanges(items, liveEdits),
  };
}
